<script setup>
import { ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAutomation } from 'dashboard/composables/useAutomation';
import { useEditableAutomation } from 'dashboard/composables/useEditableAutomation';
import { AUTOMATION_ACTION_TYPES } from './constants';
import { isDealAutomationEvent } from './dealAutomationBridge';
import AutomationRuleForm from './AutomationRuleForm.vue';

const props = defineProps({
  selectedResponse: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['saveAutomation']);

const allCustomAttributes = useMapGetter('attributes/getAttributes');
const formRef = ref(null);

const {
  automation,
  automationTypes,
  onEventChange,
  getConditionDropdownValues,
  appendNewCondition,
  appendNewAction,
  removeFilter,
  removeAction,
  resetAction,
  getActionDropdownValues,
  manifestCustomAttributes,
} = useAutomation();

const { formatAutomation } = useEditableAutomation();

const open = () => formRef.value?.open();
const close = () => formRef.value?.close();
const onSave = (payload, mode) => emit('saveAutomation', payload, mode);

watch(
  () => props.selectedResponse,
  value => {
    if (!value?.conditions) return;
    manifestCustomAttributes();

    if (isDealAutomationEvent(value.event_name)) {
      // Deal event: prefix deal_ to conditions/actions; AutomationRuleForm hydrates values async
      automation.value = {
        ...value,
        conditions: (value.conditions || []).map(c => ({
          ...c,
          attribute_key: `deal_${String(c.attribute_key || 'status').replace(/^deal_/, '')}`,
        })),
        actions: (value.actions || []).map(a => ({
          ...a,
          action_name: `deal_${String(a.action_name || 'change_stage').replace(/^deal_/, '')}`,
        })),
      };
      return;
    }

    const hasDealActions = (value.actions || []).some(a =>
      String(a.action_name || '').startsWith('deal_')
    );
    if (hasDealActions) {
      // formatAutomation crashes on deal_* action types (not in AUTOMATION_ACTION_TYPES).
      // Strip deal actions temporarily so conditions get properly hydrated (e.g. inbox_id
      // multi_select values), then restore the full action list raw so AutomationRuleForm
      // can hydrate deal action_params via its own async logic.
      const standardActions = (value.actions || []).filter(
        a => !String(a.action_name || '').startsWith('deal_')
      );
      try {
        const partial = formatAutomation(
          { ...value, actions: standardActions },
          allCustomAttributes.value,
          automationTypes,
          AUTOMATION_ACTION_TYPES
        );
        automation.value = { ...partial, actions: value.actions };
      } catch (_e) {
        automation.value = value;
      }
      return;
    }

    try {
      automation.value = formatAutomation(
        value,
        allCustomAttributes.value,
        automationTypes,
        AUTOMATION_ACTION_TYPES
      );
    } catch (_e) {
      automation.value = value;
    }
  },
  { immediate: true }
);

defineExpose({ open, close });
</script>

<template>
  <AutomationRuleForm
    ref="formRef"
    v-model:automation="automation"
    mode="edit"
    :automation-types="automationTypes"
    :get-condition-dropdown-values="getConditionDropdownValues"
    :get-action-dropdown-values="getActionDropdownValues"
    :append-new-condition="appendNewCondition"
    :append-new-action="appendNewAction"
    :remove-filter="removeFilter"
    :remove-action="removeAction"
    :reset-action="resetAction"
    :on-event-change="onEventChange"
    @save="onSave"
  />
</template>
